// Copyright ©️ 2026 eVolve MEP, LLC

import type { TaskPaginated, Task, WorkOrder } from '@evolvemep/api-ts';

import { GridMenu } from 'components/Mantine/GridMenu';
import { canUncompleteTask } from 'modules/Shop/Tasks/common';
import { EditTaskAssigneesPopover } from 'modules/Shop/Tasks/TaskViewer/EditTaskAssigneesPopover';
import { ResumeTaskModal } from 'modules/Shop/Tasks/TaskViewer/ResumeTaskModal';
import { UncompleteTaskModal } from 'modules/Shop/Tasks/TaskViewer/UncompleteTaskModal';
import { SetTaskWorkCellPopover } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/MoveWorkCell/SetTaskWorkCellPopover';
import type { WorkCellSelectProps } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/MoveWorkCell/WorkCellSelect';
import { SetTaskPredecessorsPopover } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/SetTaskPredecessorsPopover';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

type Props = {
  workOrder: WorkOrder;
  task: Task | TaskPaginated;
  onTaskUpdated: (updatedTask: Task) => void;
  onDeleteTask: () => void;
} & Pick<WorkCellSelectProps, 'facility'>;

export const EditTaskMenu = ({ workOrder, task, onTaskUpdated, onDeleteTask }: Props) => {
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const canEditTask = canEditWorkOrder('editTasks');
  const canEditWorkCell = task.taskStatusTypeName !== 'Completed';
  const canUncomplete = canUncompleteTask(task);

  return (
    <GridMenu
      options={[
        {
          value: 'resume',
          label: 'Resume task',
          hidden: task.taskStatusTypeName !== 'Completed' || canUncomplete,
        },
        {
          value: 'uncomplete',
          label: 'Uncomplete task',
          hidden: !canUncomplete,
        },
        {
          value: 'edit-assignees',
          label: `${task.assignees.length === 0 ? 'Add' : 'Edit'} assignees`,
        },
        {
          value: 'set-predecessors',
          label: 'Set predecessors',
          hidden: !canEditTask,
        },
        {
          value: 'set-work-cell',
          label: 'Assign work cell',
          hidden: !canEditWorkCell,
        },
        {
          value: 'delete',
          label: 'Delete',
          hidden: !canEditTask,
        },
      ]}
      onMenuItemSelected={(menuItemSelected) => {
        if (menuItemSelected === 'delete') {
          onDeleteTask();
          return true;
        }
        return false;
      }}
    >
      {(menuItemSelected, resetMenu) => (
        <>
          <ResumeTaskModal
            task={task}
            opened={menuItemSelected === 'resume'}
            onTaskResumed={onTaskUpdated}
            onClose={resetMenu}
          />
          <UncompleteTaskModal
            task={task}
            opened={menuItemSelected === 'uncomplete'}
            onTaskUncompleted={onTaskUpdated}
            onClose={resetMenu}
          />
          <EditTaskAssigneesPopover
            opened={menuItemSelected === 'edit-assignees'}
            onClose={resetMenu}
            onTaskUpdated={onTaskUpdated}
            task={task}
          />
          <SetTaskPredecessorsPopover
            task={task}
            onClose={resetMenu}
            onTaskUpdated={onTaskUpdated}
            opened={menuItemSelected === 'set-predecessors'}
          />
          <SetTaskWorkCellPopover
            task={task}
            onClose={resetMenu}
            onTaskUpdated={onTaskUpdated}
            opened={menuItemSelected === 'set-work-cell'}
            facility={{
              facilityId: workOrder.facilityId,
              facilityName: workOrder.facilityName,
            }}
          />
        </>
      )}
    </GridMenu>
  );
};
